import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from './config';
import { User, UserRole } from '../../types/user.types';

export interface ImpersonationState {
  isImpersonating: boolean;
  originalUserId: string;
  originalUserRole: UserRole;
  impersonatedUserId: string;
}

const IMPERSONATION_KEY = 'propcloudai_impersonation';

/**
 * Get all demo users from the database
 */
export async function getDemoUsers(): Promise<User[]> {
  try {
    // Query the users collection for demo users
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    const users: User[] = [];
    snapshot.forEach((doc) => {
      const userData = doc.data();
      // Only include demo users
      if (userData.isDemo === true) {
        users.push({ id: doc.id, ...userData } as User);
      }
    });

    // Sort by role category and name
    return users.sort((a, b) => {
      const categoryA = a.category || '';
      const categoryB = b.category || '';
      if (categoryA !== categoryB) {
        return categoryA.localeCompare(categoryB);
      }
      const nameA = a.displayName || a.email || '';
      const nameB = b.displayName || b.email || '';
      return nameA.localeCompare(nameB);
    });
  } catch (error) {
    console.error('Error fetching demo users:', error);
    throw error;
  }
}

/**
 * Get ALL users from the database (for super user impersonation)
 */
export async function getAllUsers(): Promise<User[]> {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    const users: User[] = [];
    snapshot.forEach((doc) => {
      const userData = doc.data();
      // Include all users, but mark which ones are demo
      users.push({
        id: doc.id,
        ...userData,
        isDemo: userData.isDemo || false
      } as User);
    });

    // Sort by role category, demo status, and name
    return users.sort((a, b) => {
      // First sort by demo status (real users first)
      if (a.isDemo !== b.isDemo) {
        return a.isDemo ? 1 : -1;
      }

      // Then by role category
      const categoryA = a.category || getRoleInfo(a.role).category;
      const categoryB = b.category || getRoleInfo(b.role).category;
      if (categoryA !== categoryB) {
        return categoryA.localeCompare(categoryB);
      }

      // Finally by display name
      const nameA = a.displayName || a.email || '';
      const nameB = b.displayName || b.email || '';
      return nameA.localeCompare(nameB);
    });
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
}

/**
 * Get a specific demo user by ID
 */
export async function getDemoUser(userId: string): Promise<User | null> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists() && userDoc.data()?.isDemo === true) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching demo user:', error);
    throw error;
  }
}

/**
 * Get any user by ID (for super user impersonation)
 */
export async function getAnyUserById(userId: string): Promise<User | null> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }

    return null;
  } catch (error) {
    console.error('Error fetching user by ID:', error);
    throw error;
  }
}

/**
 * Start impersonation session
 */
export function startImpersonation(
  originalUser: User,
  impersonatedUserId: string
): void {
  const state: ImpersonationState = {
    isImpersonating: true,
    originalUserId: originalUser.id,
    originalUserRole: originalUser.role,
    impersonatedUserId
  };
  
  sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
}

/**
 * End impersonation session
 */
export function endImpersonation(): void {
  sessionStorage.removeItem(IMPERSONATION_KEY);
}

/**
 * Get current impersonation state
 */
export function getImpersonationState(): ImpersonationState | null {
  const stored = sessionStorage.getItem(IMPERSONATION_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as ImpersonationState;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Check if currently impersonating
 */
export function isImpersonating(): boolean {
  const state = getImpersonationState();
  return state?.isImpersonating === true;
}

/**
 * Check if user has permission to access impersonation feature
 */
export function canImpersonate(userRole: UserRole): boolean {
  return userRole === UserRole.PROPCLOUDAI_SUPER_USER ||
         userRole === UserRole.HQ_MANAGER;
}

/**
 * Roles an HQ Manager may impersonate.
 *
 * Anything in this set is "below" the HQ Manager in the org hierarchy —
 * consultants, agents, bond/admin/advertising/viewing/call-centre staff,
 * plus deprecated legacy roles still present on older user records.
 *
 * EXPLICITLY excluded:
 *   • HQ_MANAGER itself (no peer-impersonation)
 *   • PROPCLOUDAI_SUPER_USER and other propcloudai-tier roles (no upward)
 *
 * Adding LVL2 (HQ_INDEPENDENT_CONSULTANT_LVL2) closes the bug where HQ
 * Managers could impersonate Pieter Piek's LVL1 colleagues but not Pieter
 * himself, which blocked legitimate admin work (re-capturing the deals
 * that the old expireSellerSignedDeals bug deleted in May 2026).
 */
const HQ_MANAGER_IMPERSONATABLE_ROLES = new Set<UserRole>([
  UserRole.HQ_CONSULTANT_LVL1,
  UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
  UserRole.HQ_REFERRAL_MANDATE,
  UserRole.HQ_BONDS,
  UserRole.HQ_ADVERTISING,
  UserRole.HQ_VIEWING_AGENT,
  UserRole.HQ_VIEWING_COORDINATOR,
  UserRole.HQ_CALL_CENTRE_AGENT,
  UserRole.HQ_ADMIN,
  // AI agents: impersonation is the intended way for admins to inspect
  // "what ARIA's account sees" — the account itself never logs in.
  UserRole.AI_AGENT_LVL1,
  // Deprecated roles still seen on legacy user records:
  UserRole.VIEWING_AGENT,
  UserRole.INDEPENDENT_PARTNER_LVL3,
  UserRole.PARTNER_AGENT_LVL3,
]);

/**
 * Check if a user with specific role can impersonate a target user
 * @param impersonatorRole - Role of the user attempting to impersonate
 * @param targetRole - Role of the user being impersonated
 * @returns true if impersonation is allowed, false otherwise
 */
export function canImpersonateUser(
  impersonatorRole: UserRole,
  targetRole: UserRole
): boolean {
  // Super users can impersonate anyone
  if (impersonatorRole === UserRole.PROPCLOUDAI_SUPER_USER) {
    return true;
  }

  // HQ Managers can impersonate any subordinate role — every consultant,
  // agent, admin, bonds, advertising, viewing or call-centre user — but
  // never another HQ Manager and never a PropCloudAI super-admin.
  if (impersonatorRole === UserRole.HQ_MANAGER) {
    return HQ_MANAGER_IMPERSONATABLE_ROLES.has(targetRole);
  }

  // No other roles have impersonation permissions
  return false;
}

/**
 * Get role display info for demo users
 */
export function getRoleInfo(role: UserRole): {
  category: string;
  color: string;
  commission?: number;
} {
  const roleCategories: Record<string, { category: string; color: string; commission?: number }> = {
    [UserRole.PROPCLOUDAI_SUPER_USER]: { category: 'PropCloudAI System', color: 'purple' },
    [UserRole.PROPCLOUDAI_USER_MANAGEMENT]: { category: 'PropCloudAI System', color: 'purple' },
    [UserRole.PROPCLOUDAI_CRM]: { category: 'PropCloudAI System', color: 'purple' },
    [UserRole.PROPCLOUDAI_ADVERTISING]: { category: 'PropCloudAI System', color: 'purple' },
    [UserRole.HQ_MANAGER]: { category: 'PropInvest HQ', color: 'blue' },
    [UserRole.HQ_ADMIN]: { category: 'PropInvest HQ', color: 'blue' },
    [UserRole.HQ_BONDS]: { category: 'PropInvest HQ', color: 'blue' },
    [UserRole.HQ_CONSULTANT_LVL1]: { category: 'PropInvest HQ', color: 'green', commission: 30 },
    [UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]: { category: 'PropInvest HQ', color: 'green', commission: 50 },
    [UserRole.HQ_REFERRAL_MANDATE]: { category: 'PropInvest HQ', color: 'green', commission: 5 },
    [UserRole.INDEPENDENT_PARTNER_LVL3]: { category: 'Independent Partner', color: 'orange', commission: 70 },
    [UserRole.PARTNER_AGENT_LVL3]: { category: 'Partner Office', color: 'orange', commission: 30 },
    [UserRole.VIEWING_AGENT]: { category: 'PropInvest HQ', color: 'teal' },
    [UserRole.HQ_CALL_CENTRE_AGENT]: { category: 'PropInvest HQ', color: 'teal' },
    [UserRole.HQ_VIEWING_COORDINATOR]: { category: 'PropInvest HQ', color: 'teal' },
    [UserRole.HQ_ADVERTISING]: { category: 'PropInvest HQ', color: 'pink' },
    [UserRole.AI_AGENT_LVL1]: { category: 'PropInvest HQ', color: 'gray', commission: 0 }
  };
  
  return roleCategories[role] || { category: 'Unknown', color: 'gray' };
}